import React, { useEffect, useState, useRef } from "react";
import httpClient from "../../_util/api";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, IconButton, Button, Dialog, DialogTitle, DialogContent,
  FormControl, InputLabel, Select, MenuItem, TextField, DialogActions, Box, Autocomplete,
  Tabs, Tab, InputAdornment
} from "@mui/material";
import { Visibility, HourglassTop, TaskAlt, HighlightOff, Search, ReceiptLong } from "@mui/icons-material";
import { PDFViewer } from '@react-pdf/renderer';
import ApplicationView from './ApplicationView'
import ButtonMain from "../../components/Button";
import { hideLoader, showLoader } from "../../Store/mainSlice";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import { useDispatch } from 'react-redux'
import FileUpload from "../../components/FileUpload";
import { UploadImagetoFb } from '../../Hooks/uploadImage'
import EditStudentModal from "../../components/EditStudentModal";
import useDebounce from "../../_util/UseDebounce";

// Per role: the first tab is the status waiting on this user's action; the
// second lists what they've already handled (view-only). For reviewer/approver,
// "completed" is resolved server-side to applications they've acted on.
const ROLE_TABS = {
  reviewer: [
    { value: "pending", label: "In Process", icon: <HourglassTop fontSize="small" /> },
    { value: "completed", label: "Completed", icon: <TaskAlt fontSize="small" /> },
    { value: "rejected-by-me", label: "Rejected", icon: <HighlightOff fontSize="small" /> },
  ],
  approver: [
    { value: "process", label: "In Process", icon: <HourglassTop fontSize="small" /> },
    { value: "completed", label: "Completed", icon: <TaskAlt fontSize="small" /> },
    { value: "rejected-by-me", label: "Rejected", icon: <HighlightOff fontSize="small" /> },
  ],
  vendor: [
    { value: "approved", label: "In Process", icon: <HourglassTop fontSize="small" /> },
    { value: "paid", label: "Completed", icon: <TaskAlt fontSize="small" /> },
  ],
};

const BRAND_GREEN = "#4caf50";

const tabsSx = {
  mb: 2.5,
  mx: -2,
  px: 2,
  minHeight: 0,
  borderBottom: "1px solid",
  borderColor: "divider",
  "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", bgcolor: BRAND_GREEN },
  "& .MuiTabs-flexContainer": { gap: 1 },
  "& .MuiTab-root": {
    minHeight: 48,
    px: 2,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 15,
    color: "text.secondary",
    "&:hover": { color: "#2e7d32" },
  },
  "& .MuiTab-root.Mui-selected": { color: "#2e7d32" },
};

const TabLabel = ({ label, count, selected }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
    {label}
    {count !== undefined && (
      <Box
        component="span"
        sx={{
          minWidth: 22,
          height: 20,
          px: 0.75,
          borderRadius: "10px",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: "20px",
          textAlign: "center",
          bgcolor: selected ? BRAND_GREEN : "#eceff1",
          color: selected ? "#fff" : "#546e7a",
        }}
      >
        {count}
      </Box>
    )}
  </Box>
);

const UserTable = ({ role }) => {
  const challanPhotoRef = useRef();
  const dispatch = useDispatch();
  const snackbar_Ref = useRef();
  const tabs = ROLE_TABS[role] || ROLE_TABS.vendor;
  const [activeTab, setActiveTab] = useState(tabs[0].value);
  const [tabCounts, setTabCounts] = useState({});

  // Total per tab (unfiltered), shown as a badge on each tab
  const fetchTabCounts = async () => {
    const results = await Promise.all(
      tabs.map((t) =>
        httpClient
          .get(`/students/getReviewApplications/${t.value}?page=1&limit=1&search=`)
          .then((res) => [t.value, res?.data?.pagination?.totalApplications ?? 0])
          .catch(() => [t.value, undefined])
      )
    );
    setTabCounts(Object.fromEntries(results));
  };
  const status = activeTab;
  // The challan is uploaded by the partner, so only partners see it
  const showChallanColumn = role === "vendor" && activeTab === "paid";
  const userRole = role == "reviewer" ? "process" : "approved";
  const [data, setData] = useState([]);
  const [applicationData, setApplicationData] = useState(null);
  // Only applications still waiting on this user can be acted on or edited
  const canTakeAction = applicationData?.status === tabs[0].value;
  const canChangePartner = (role === "reviewer" || role === "approver")
    && activeTab === "completed"
    && ["process", "approved"].includes(applicationData?.status);
  const canChangeFee = role === "approver" && activeTab === "completed" && applicationData?.status === "approved";
  const canUpdateChallan = role === "vendor" && applicationData?.status === "paid";
  const canCorrect = canChangePartner || canUpdateChallan;
  const [isCorrectionOpen, setIsCorrectionOpen] = useState(false);
  const [correction, setCorrection] = useState({ vendorId: "", approvedFee: "", remarks: "" });
  const [universities, setUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [isViewDetails, setIsViewDetails] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditStudentModalOpen, setIsEditStudentModalOpen] = useState(false);
  const [editStudentTarget, setEditStudentTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalApplications, setTotalApplications] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const latestRequestRef = useRef(0);
  const [vendorList, setVendorList] = useState([]);
  const [remarks, setRemarks] = useState(applicationData?.status);
  const [actionStatus, setActionStatus] = useState('');
  const [vendor, setVendor] = useState('');
  const [percent, setPercent] = useState(100);
  const [otherAmount, setOtherAmount] = useState(0);

  const [challanPhoto, setchallanPhoto] = useState(
    {
      fileName: "",
      url: ""
    },
  )
  const getAllApplicationByStatus = async (pageNumber = page, limit = rowsPerPage, univserity = selectedUniversity, searchTerm = debouncedSearch) => {
    // Only the most recent request may update the table; an older, slower
    // response must not overwrite newer results.
    const requestId = ++latestRequestRef.current;
    try {
      const searchParam = (searchTerm && searchTerm.trim()) ? searchTerm.trim() : (univserity ?? "");
      const res = await httpClient.get(
        `/students/getReviewApplications/${status}?page=${pageNumber + 1}&limit=${limit}&search=${encodeURIComponent(searchParam)}`
      );
      if (requestId !== latestRequestRef.current) return;
      if (res.status === 200) {
        setData(res.data.applications);
        setTotalApplications(res.data.pagination.totalApplications);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getAllPakistaniUni = async () => {
    let res = await httpClient.get('students/getAllPakUniversities').catch((error) => {
      console.log("error", error)
    })
    if (res?.status === 200) {
      const universityNames = res.data.map(item => item.name);
      setUniversities(['Other', ...universityNames]);
    }
  }

  const getVendorListHandler = async () => {
    try {
      const res = await httpClient.get(`/user/role/vendor`);
      if (res.status === 200) {
        setVendorList(res.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const selectedVendor = vendorList.find(v => v.firstName === vendor);

  const approvedFee = percent == "other"
    ? otherAmount
    : (applicationData?.tuttionFee * percent) / 100;

  const totalFee = role === "approver" 
    ? `Total Fee: ${applicationData?.tuttionFee} | Approved Fee: ${approvedFee}`
    : role === "vendor" 
    ? applicationData?.approvedFee
    : applicationData?.tuttionFee;

  const vendorData =
   { 
    id: selectedVendor?._id || "", 
    challanFile: challanPhoto,
  }

  const handleSubmitAction = async () => {
    dispatch(showLoader())
    const applcaitionData = {
      applicationId: applicationData?._id,
      userId: applicationData?.userId,
      amount: applicationData?.approvedFee,
      approvedFee,
      applicationNumber: applicationData?.applicationNumber,
      status: actionStatus == "approved" ? userRole : actionStatus,
      vendor: vendorData,
      remarks,
    }
    const res = await httpClient.post('/students/approveStudent', applcaitionData).catch((error) => {
    })
    if (res?.status === 200) {
      dispatch(hideLoader());
      setIsModalOpen(false);
      snackbar_Ref.current.showMessage("success", res.data.message, "", "i-chk-circle");
      setData((prevData) =>
        prevData.filter(item => item._id !== applicationData?._id)
      );
      fetchTabCounts();
    } else {
      dispatch(hideLoader());
      setIsModalOpen(false);
      snackbar_Ref.current.showMessage("error", res.data.message, "", "i-chk-circle");
    }
  };
  
  const openCorrection = () => {
    setCorrection({
      vendorId: applicationData?.vendor?.id || "",
      approvedFee: applicationData?.approvedFee ?? "",
      remarks: "",
    });
    setchallanPhoto({ fileName: "", url: "" });
    setIsCorrectionOpen(true);
  };

  const showError = (res) =>
    snackbar_Ref.current?.showMessage("error", res?.data?.message || "Update failed", "", "i-chk-circle");

  const handleSubmitCorrection = async () => {
    const appId = applicationData?._id;
    const { vendorId, approvedFee: newFee, remarks: correctionRemarks } = correction;
    const partnerChanged = canChangePartner && vendorId && String(vendorId) !== String(applicationData?.vendor?.id || "");
    const feeChanged = canChangeFee && newFee !== "" && Number(newFee) !== Number(applicationData?.approvedFee);
    const challanChanged = canUpdateChallan && challanPhoto.url;

    if (!partnerChanged && !feeChanged && !challanChanged) {
      snackbar_Ref.current?.showMessage("error", "Nothing to update", "", "i-chk-circle");
      return;
    }

    // Reflect each successful step right away, so a later failing step doesn't
    // hide a change that was already saved.
    const applyUpdate = (updated) => {
      setApplicationData((prev) => ({
        ...prev,
        vendor: updated.vendor ?? prev.vendor,
        approvedFee: updated.approvedFee ?? prev.approvedFee,
        notes: updated.notes ?? prev.notes,
      }));
      setData((prev) => prev.map((item) => (item._id === appId ? { ...item, ...updated } : item)));
    };

    const requests = [
      partnerChanged && [`/students/application/${appId}/partner`, { vendorId }],
      feeChanged && [`/students/application/${appId}/approved-fee`, { approvedFee: Number(newFee) }],
      challanChanged && [`/students/application/${appId}/challan`, { challanFile: challanPhoto }],
    ].filter(Boolean);

    dispatch(showLoader());
    try {
      for (const [url, body] of requests) {
        const res = await httpClient
          .patch(url, { ...body, remarks: correctionRemarks })
          .catch((err) => err?.response);
        if (res?.status !== 200) return showError(res);
        applyUpdate(res.data.application);
      }
      setIsCorrectionOpen(false);
      snackbar_Ref.current?.showMessage("success", "Application updated successfully", "", "i-chk-circle");
    } finally {
      dispatch(hideLoader());
    }
  };

  const uploadPhotoHandler = (ref) => {
    ref?.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    if (file.type !== "application/pdf") {
      alert("Please upload a PDF file.");
      return;
    }
  
    dispatch(showLoader());
    try {
      const uploadedFile = await UploadImagetoFb(event);
      const newFile = {
        fileName: file.name,
        url: uploadedFile.url,
      };
      setchallanPhoto(newFile);
    } catch (err) {
      console.error("Upload error:", err);
      alert("File upload failed. Please try again.");
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await httpClient.get(
          `/students/getAllApplicationDocuments/${applicationData?.userId}`
        );
        const { data } = res.data;

        if (res.status === 200) {
          setApplicationData(prevData => {
            return {
              ...prevData,
              ...data,
            };
          });
          setTotalApplications(res.data.pagination?.totalApplications ?? totalApplications);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isViewDetails && applicationData?.userId) {
      fetchData();
    }
  }, [isViewDetails, applicationData?.userId]);
  
  useEffect(() => {
    getAllPakistaniUni();
    getVendorListHandler();
    fetchTabCounts();
  }, []);

  // Pre-select the partner already assigned to this application (e.g. by the
  // reviewer) so the approver sees it instead of a blank dropdown, and reset
  // to blank when switching to an application with no partner assigned yet.
  useEffect(() => {
    const assignedVendorId = applicationData?.vendor?.id;
    const assignedVendor = vendorList.find(v => v._id === assignedVendorId);
    setVendor(assignedVendor?.firstName || '');
  }, [applicationData, vendorList]);

  // Wait for the user to stop typing before searching, and start from the first page.
  useDebounce(() => {
    setDebouncedSearch(search);
    setPage(0);
  }, [search], 500);

  useEffect(() => {
    getAllApplicationByStatus(page, rowsPerPage, selectedUniversity, debouncedSearch);
  }, [page, rowsPerPage, selectedUniversity, debouncedSearch, status]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  if (isViewDetails && applicationData) {
    return (
      <>
      <CustomizedSnackbars ref={snackbar_Ref} />
      <Box display="flex" justifyContent="space-between" alignItems="center" m={2}>
        <ButtonMain variant="white" onClick={() => setIsViewDetails(false)}>
          Back to List
        </ButtonMain>
        <Box display="flex" gap={1}>
          {role === "reviewer" && canTakeAction && (
            <ButtonMain
              variant="white"
              onClick={() => {
                setEditStudentTarget({ ...applicationData, _id: applicationData.userId });
                setIsEditStudentModalOpen(true);
              }}
            >
              Edit Student
            </ButtonMain>
          )}
          {canTakeAction && (
            <ButtonMain onClick={() => setIsModalOpen(true)}>
              Take Action
            </ButtonMain>
          )}
          {canCorrect && (
            <ButtonMain onClick={openCorrection}>
              {canUpdateChallan ? "Update Challan" : "Update"}
            </ButtonMain>
          )}
        </Box>
      </Box>
      {role === "vendor" && applicationData?.vendor?.challanFile?.url && (
        <Box
          sx={{
            p: 2, mx: 2, mb: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
            bgcolor: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 2, color: "#1b5e20",
          }}
        >
          <ReceiptLong sx={{ color: "#2e7d32" }} />
          <strong>Paid Challan</strong>
          <Box
            component="a"
            href={applicationData.vendor.challanFile.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ml: "auto", px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: "#fff",
              border: "1px solid #a5d6a7", color: "#2e7d32", fontWeight: 600, textDecoration: "none",
              "&:hover": { bgcolor: "#c8e6c9" },
            }}
          >
            {applicationData.vendor.challanFile.fileName || "View challan"}
          </Box>
        </Box>
      )}
      <Paper>
        <ApplicationView
          data={applicationData}
          editable={role === "reviewer" && canTakeAction}
          hidePrivateSections={role === "vendor"}
          applicationId={applicationData?._id}
          universities={universities}
          onSave={async (fields) => {
            const res = await httpClient
              .patch(`/students/application/${applicationData?._id}`, fields)
              .catch((err) => err?.response);

            if (res?.status === 200) {
              setApplicationData((prev) => ({ ...prev, ...fields }));
              snackbar_Ref.current?.showMessage("success", "Application updated successfully", "", "i-chk-circle");
            } else {
              snackbar_Ref.current?.showMessage("error", res?.data?.message || "Update failed", "", "i-error-circle");
              throw new Error(res?.data?.message || "Update failed");
            }
          }}
        />
      </Paper>
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Take Action on Application</DialogTitle>
          <DialogContent sx={{display: "flex", gap: "4px", flexDirection: "column"}}>
          <TextField
            fullWidth
            label="Fee Amount"
            value={totalFee}
            sx={{ mt: 4 }}
            InputProps={{
              readOnly: true,
            }}
          />

            {role === "vendor" ? (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={actionStatus}
                    onChange={(e) => setActionStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                </FormControl>

                <FileUpload
                  ref={challanPhotoRef}
                  label="Upload Paid Challan"
                  file={challanPhoto}
                  onChange={handleFileChange}
                  onClick={() => uploadPhotoHandler(challanPhotoRef)}
                  onRemove={() =>
                    setchallanPhoto({
                      fileName: "",
                      url: "",
                    })
                  }
                />
              </>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={actionStatus}
                  onChange={(e) => setActionStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>
            )}

            {role === "approver" && (
              <>
              <FormControl fullWidth>
              <InputLabel id="percent-label">Fee Approved in Percent</InputLabel>
              <Select
                labelId="percent-label"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                label="Fee Approved in Percent"
              >
                {[25, 50, 75, 100].map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}%
                  </MenuItem>
                ))}
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            {percent == "other" && (
              <TextField
                sx={{marginTop: "8px"}}
                required
                label="Amount"
                type="number"
                value={otherAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value <= applicationData?.tuttionFee) {
                    setOtherAmount(value);
                  }
                }}
              />
            )}
          </>
        )}

            {(role === "reviewer" || role === "approver") && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Partner</InputLabel>
                <Select
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  label="Vendor"
                >
                  {vendorList.map((v) => (
                    <MenuItem key={v._id} value={v.firstName}>
                      {v.firstName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Remarks"
              multiline
              rows={4}
              fullWidth
              margin="normal"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </DialogContent>

        <DialogActions>
          <ButtonMain variant="white" onClick={() => setIsModalOpen(false)}>Cancel</ButtonMain>
          <ButtonMain  onClick={handleSubmitAction}>Submit</ButtonMain>
        </DialogActions>
      </Dialog>

      <Dialog open={isCorrectionOpen} onClose={() => setIsCorrectionOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{canUpdateChallan ? "Update Paid Challan" : "Update Application"}</DialogTitle>
        <DialogContent sx={{ display: "flex", gap: "4px", flexDirection: "column" }}>
          {canChangePartner && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Partner</InputLabel>
              <Select
                value={correction.vendorId}
                onChange={(e) => setCorrection((prev) => ({ ...prev, vendorId: e.target.value }))}
                label="Partner"
              >
                {vendorList.map((v) => (
                  <MenuItem key={v._id} value={v._id}>
                    {v.firstName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {canChangeFee && (
            <TextField
              fullWidth
              margin="normal"
              label="Approved Fee"
              type="number"
              value={correction.approvedFee}
              helperText={`Tuition Fee: ${applicationData?.tuttionFee ?? "-"}`}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || Number(value) <= (applicationData?.tuttionFee ?? Infinity)) {
                  setCorrection((prev) => ({ ...prev, approvedFee: value }));
                }
              }}
            />
          )}

          {canUpdateChallan && (
            <FileUpload
              ref={challanPhotoRef}
              label="Upload Correct Paid Challan"
              file={challanPhoto}
              onChange={handleFileChange}
              onClick={() => uploadPhotoHandler(challanPhotoRef)}
              onRemove={() => setchallanPhoto({ fileName: "", url: "" })}
            />
          )}

          <TextField
            label="Reason for update"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={correction.remarks}
            onChange={(e) => setCorrection((prev) => ({ ...prev, remarks: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <ButtonMain variant="white" onClick={() => setIsCorrectionOpen(false)}>Cancel</ButtonMain>
          <ButtonMain onClick={handleSubmitCorrection}>Save</ButtonMain>
        </DialogActions>
      </Dialog>

      <EditStudentModal
        open={isEditStudentModalOpen}
        student={editStudentTarget}
        onClose={() => setIsEditStudentModalOpen(false)}
        onSaved={(updated) => setApplicationData((prev) => ({ ...prev, ...updated }))}
        onNotify={(type, msg) => snackbar_Ref.current?.showMessage(type, msg, "", "i-chk-circle")}
        allowPasswordChange={false}
      />
      </>
    );
  }

  return (
  <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
    <Tabs
      value={activeTab}
      onChange={(e, v) => { setActiveTab(v); setPage(0); }}
      variant="scrollable"
      scrollButtons={false}
      sx={tabsSx}
    >
      {tabs.map((t) => (
        <Tab
          key={t.value}
          value={t.value}
          icon={t.icon}
          iconPosition="start"
          label={<TabLabel label={t.label} count={tabCounts[t.value]} selected={activeTab === t.value} />}
        />
      ))}
    </Tabs>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <TextField
        size="small"
        placeholder="Search by email or application number"
        variant="outlined"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
        sx={{ flex: "1 1 320px", maxWidth: { sm: 420 } }}
      />
      <Autocomplete
        size="small"
        options={universities}
        value={selectedUniversity}
        onChange={(event, newValue) => {
          setSelectedUniversity(newValue);
          setPage(0);
        }}
        sx={{ flex: "1 1 260px", maxWidth: { sm: 320 }, ml: { md: "auto" } }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Filter by institution"
            variant="outlined"
          />
        )}
      />
    </Box>
    <TableContainer>
      <Table>
        <TableHead sx={{ backgroundColor: "#4caf50" }}>
          <TableRow>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Application No</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Enrollment #</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Institution</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Semester</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Tuition Fee</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Approved Fee</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Submission Date</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
            {showChallanColumn && (
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paid Challan</TableCell>
            )}
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showChallanColumn ? 10 : 9} align="center">
                No applications found
              </TableCell>
            </TableRow>
          ) : (
            data.map((item) => (
              <TableRow
                onClick={() => {
                  const selectedApp = data.find(e => e._id === item._id);
                  setApplicationData(selectedApp);
                  setIsViewDetails(true);
                }}
                key={item._id}
                sx={{ cursor: "pointer" }}
              >
              <TableCell>
                <Box
                  component="span"
                  sx={{
                    color: item.isReturning ? 'success.main' : 'inherit',
                    fontWeight: item.isReturning ? 600 : 'inherit',
                    border: item.isReturning ? '1px solid' : 'none',
                    borderColor: 'success.main',
                    borderRadius: '4px',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {item.applicationNumber}
                </Box>
                {item.gpaFlag?.level && item.gpaFlag.level !== 'none' && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      color: 'error.main',
                      border: '1px solid',
                      borderColor: 'error.main',
                      borderRadius: '4px',
                      px: 0.75,
                      py: 0.25,
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Low GPA
                  </Box>
                )}
              </TableCell>
                <TableCell>{item.enrollmentNo || "-"}</TableCell>
                <TableCell>{item.otherInstitutionName || item.institutionName}</TableCell>
                <TableCell>{item.grade}</TableCell>
                <TableCell>{item.tuttionFee}</TableCell>
                <TableCell>{item.approvedFee}</TableCell>
                <TableCell>{item.applicationSubmitDate}</TableCell>
                <TableCell style={{ textTransform: 'capitalize' }}>{item.status}</TableCell>
                {showChallanColumn && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {item.vendor?.challanFile?.url ? (
                      <a href={item.vendor.challanFile.url} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    ) : "-"}
                  </TableCell>
                )}
                <TableCell>
                  <IconButton color="primary"><Visibility /></IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <TablePagination
      component="div"
      count={totalApplications}
      page={page}
      onPageChange={handleChangePage}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      rowsPerPageOptions={[5, 10, 25, 50]}
    />
  </Paper>
);
};

export default UserTable;
